import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';


const RecurringScheduleModal = ({ isOpen, onClose, onSave, initialData = null }) => {
  const [formData, setFormData] = useState({
    recurringType: 'none',
    weeklyDays: [],
    monthlyDay: '',
    customInterval: '',
    customUnit: 'days',
    endDate: '',
    endAfterOccurrences: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        recurringType: initialData?.recurringType || 'none',
        weeklyDays: initialData?.weeklyDays || [],
        monthlyDay: initialData?.monthlyDay || '',
        customInterval: initialData?.customInterval || '',
        customUnit: initialData?.customUnit || 'days',
        endDate: initialData?.endDate || '',
        endAfterOccurrences: initialData?.endAfterOccurrences || ''
      });
    } else {
      setFormData({
        recurringType: 'none',
        weeklyDays: [],
        monthlyDay: '',
        customInterval: '',
        customUnit: 'days',
        endDate: '',
        endAfterOccurrences: ''
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const recurringTypeOptions = [
    { value: 'none', label: 'Does Not Repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Custom' }
  ];

  const customUnitOptions = [
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' }
  ];

  const weekDays = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleWeeklyDayToggle = (dayValue) => {
    setFormData(prev => {
      const weeklyDays = prev?.weeklyDays?.includes(dayValue)
        ? prev?.weeklyDays?.filter(d => d !== dayValue)
        : [...prev?.weeklyDays, dayValue];
      return { ...prev, weeklyDays };
    });
  };

  const validate = () => {
    const newErrors = {};

    if (formData?.recurringType === 'weekly' && formData?.weeklyDays?.length === 0) {
      newErrors.weeklyDays = 'Please select at least one day';
    }

    if (formData?.recurringType === 'monthly' && !formData?.monthlyDay) {
      newErrors.monthlyDay = 'Please select a day of the month';
    }

    if (formData?.recurringType === 'custom') {
      if (!formData?.customInterval || formData?.customInterval < 1) {
        newErrors.customInterval = 'Please enter a valid interval';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (validate()) {
      onSave(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      recurringType: 'none',
      weeklyDays: [],
      monthlyDay: '',
      customInterval: '',
      customUnit: 'days',
      endDate: '',
      endAfterOccurrences: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-card rounded-xl border border-border shadow-warm-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="repeat" size={20} className="text-primary" />
            </div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              Recurring Schedule
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <Icon name="x" size={24} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <Select
            label="Repeat Pattern"
            placeholder="Select recurring type"
            options={recurringTypeOptions}
            value={formData?.recurringType}
            onChange={(value) => handleChange('recurringType', value)}
          />

          {formData?.recurringType === 'weekly' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Repeat On
              </label>
              <div className="flex flex-wrap gap-2">
                {weekDays?.map(day => (
                  <button
                    key={day?.value}
                    type="button"
                    onClick={() => handleWeeklyDayToggle(day?.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                      formData?.weeklyDays?.includes(day?.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {day?.label}
                  </button>
                ))}
              </div>
              {errors?.weeklyDays && (
                <p className="text-sm text-destructive mt-1">{errors?.weeklyDays}</p>
              )}
            </div>
          )}

          {formData?.recurringType === 'monthly' && (
            <Input
              label="Day of Month"
              type="number"
              placeholder="Enter day (1-31)"
              value={formData?.monthlyDay}
              onChange={(e) => handleChange('monthlyDay', e?.target?.value)}
              error={errors?.monthlyDay}
              min="1"
              max="31"
            />
          )}

          {formData?.recurringType === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Every"
                type="number"
                placeholder="Enter number"
                value={formData?.customInterval}
                onChange={(e) => handleChange('customInterval', e?.target?.value)}
                error={errors?.customInterval}
                min="1"
              />
              <Select
                label="Unit"
                placeholder="Select unit"
                options={customUnitOptions}
                value={formData?.customUnit}
                onChange={(value) => handleChange('customUnit', value)}
              />
            </div>
          )}

          {formData?.recurringType !== 'none' && (
            <div className="space-y-4 pt-4 border-t border-border">
              <h3 className="text-sm font-medium text-foreground">End Repeat (Optional)</h3>
              
              <Input
                label="End Date"
                type="date"
                placeholder="Select end date"
                value={formData?.endDate}
                onChange={(e) => handleChange('endDate', e?.target?.value)}
              />

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Or after</span>
                <Input
                  type="number"
                  placeholder="#"
                  value={formData?.endAfterOccurrences}
                  onChange={(e) => handleChange('endAfterOccurrences', e?.target?.value)}
                  min="1"
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">occurrences</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
            >
              Save Schedule
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecurringScheduleModal;